import { FC, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/lib';
import { BaseModal } from '@/lib/Modals';
import { CREDIT_SALES_CONTACT, CreditPackageTier, formatEuroNet } from '../config';
import { formatCredits } from '../utils/formatCredits';

export type CreditRequestPackage = {
  name: string;
  volume: number;
  priceNet: number | null;
  channelLabel: string;
};

type CampaignsCreditRequestModalProps = {
  open: boolean;
  request: CreditRequestPackage | null;
  onClose: VoidFunction;
};

export const CampaignsCreditRequestModal: FC<CampaignsCreditRequestModalProps> = ({ open, request, onClose }) => {
  const { t, i18n } = useTranslation('campaign');
  const [copied, setCopied] = useState(false);
  const titleId = useId();

  if (!open || !request) {
    return null;
  }

  const handleCopyPhone = async () => {
    try {
      await navigator.clipboard.writeText(CREDIT_SALES_CONTACT.phone);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <BaseModal
      onClose={onClose}
      aria-labelledby={titleId}
      classNames={{
        dialog: 'max-w-md w-full',
        content: 'min-w-0 w-full gap-0 justify-start',
      }}
    >
      <div className='flex w-full flex-col'>
        <h2 id={titleId} className='text-xl font-extrabold text-slate-900'>
          {t('credits.requestModal.title')}
        </h2>
        <p className='mt-2 text-sm text-secondary-600'>
          {t('credits.requestModal.expectation', { team: t('credits.requestModal.teamLabel') })}
        </p>

        <dl className='mt-5 space-y-3 rounded-xl border border-secondary-border bg-secondary-background/40 p-4 text-sm'>
          <div className='flex justify-between gap-4'>
            <dt className='text-secondary-500'>{t('credits.requestModal.channel')}</dt>
            <dd className='font-semibold text-slate-900'>{request.channelLabel}</dd>
          </div>
          <div className='flex justify-between gap-4'>
            <dt className='text-secondary-500'>{t('credits.requestModal.package')}</dt>
            <dd className='font-semibold text-slate-900'>{request.name}</dd>
          </div>
          {request.volume > 0 ? (
            <div className='flex justify-between gap-4'>
              <dt className='text-secondary-500'>{t('credits.requestModal.volume')}</dt>
              <dd className='font-semibold text-slate-900'>{formatCredits(request.volume, i18n.language)}</dd>
            </div>
          ) : null}
          {request.priceNet != null ? (
            <div className='flex justify-between gap-4'>
              <dt className='text-secondary-500'>{t('credits.requestModal.price')}</dt>
              <dd className='font-semibold text-slate-900'>
                {formatEuroNet(request.priceNet, i18n.language)} {t('credits.requestModal.vatNote')}
              </dd>
            </div>
          ) : null}
        </dl>

        <div className='mt-5 rounded-xl border border-secondary-border p-4'>
          <p className='text-xs font-semibold uppercase tracking-[0.12em] text-secondary-500'>{t('credits.requestModal.contactLabel')}</p>
          <div className='mt-2 flex items-center justify-between gap-3'>
            <p className='select-all font-mono text-base font-bold text-slate-900'>{CREDIT_SALES_CONTACT.phone}</p>
            <Button intent='secondary' text={copied ? t('credits.requestModal.copied') : t('credits.requestModal.copy')} onClick={handleCopyPhone} />
          </div>
          <p className='mt-2 text-xs text-secondary-500'>{t('credits.requestModal.businessHours')}</p>
        </div>

        <div className='mt-6 flex justify-end'>
          <Button intent='primary' text={t('credits.requestModal.close')} onClick={onClose} />
        </div>
      </div>
    </BaseModal>
  );
};

export const toCreditRequestPackage = (
  tier: Pick<CreditPackageTier, 'name' | 'volume' | 'priceNet'>,
  channelLabel: string
): CreditRequestPackage => ({
  name: tier.name,
  volume: tier.volume,
  priceNet: tier.priceNet,
  channelLabel,
});
